import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import axios from 'axios'
import { useGetIdentity } from '@refinedev/core'
import { isRegenerableOpportunity } from '@/utils/opportunityRegen'

const apiUrl = import.meta.env.VITE_API_URL

export interface FunderRegenPanelProps {
  /** funder uuid — passed to /generate-profiles/{funder_uuid} */
  targetUuid: string
  /** merged-pending funder description (current overlaid with proposed payload) */
  mergedDescription: string
  /** current saved funder description — shown in diff context */
  currentFunderDescription: string
  /** proposed edit id — used to set inline_regen_at marker after save (fs-3gx) */
  proposedEditId: number
  /**
   * Reports whether the panel holds regenerated output for one or more opps
   * that has NOT been saved yet (GH#1630). The parent modal disables its
   * Approve/Reject while this is true so a stray click can't leave the affected
   * opportunity profiles stale.
   */
  onUnsavedChange?: (dirty: boolean) => void
  /**
   * Reports whether the reviewer has completed a fan-out regeneration for this
   * funder edit — i.e. EVERY targeted opp regenerated and saved (the marker
   * fired). The parent modal uses it to gate Approve from the outset (GH#1849
   * P1). A partial save does not count as completed.
   */
  onCompletedChange?: (completed: boolean) => void
}

interface ActiveOpp {
  uuid: string
  fund_name: string | null
  profile_text_length: number
  tags: string[]
  flag_for_updates: string | null
}

type OppStatus = 'idle' | 'profile_ok' | 'profile_error' | 'tag_ok' | 'tag_error' | 'saved' | 'save_error'

interface OppRow {
  uuid: string
  fund_name: string | null
  originalProfileLength: number
  originalTags: string[]
  newProfileText: string
  newTags: string[]
  generationFingerprint: string
  profileStatus: 'idle' | 'ok' | 'error'
  profileError: string | null
  tagStatus: 'idle' | 'ok' | 'error'
  tagError: string | null
  saveStatus: 'idle' | 'saving' | 'saved' | 'error'
  saveError: string | null
  profileExpanded: boolean
}

/** Run promises in batches, up to `limit` concurrent */
async function runWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  limit: number,
): Promise<T[]> {
  const results: T[] = []
  let i = 0
  async function worker() {
    while (i < tasks.length) {
      const idx = i++
      results[idx] = await tasks[idx]()
    }
  }
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, worker)
  await Promise.all(workers)
  return results
}

/**
 * Panel rendered inside the proposed-edits review dialog when the selected
 * edit is for a funder and the diff includes description.
 *
 * Triggers fan-out profile + tag regeneration for all active opportunities
 * under the funder, shows a per-opp results table, and lets the admin save
 * before approving the original proposed_edit.
 */
export const FunderRegenPanel: React.FC<FunderRegenPanelProps> = ({
  targetUuid,
  mergedDescription,
  currentFunderDescription,
  proposedEditId,
  onUnsavedChange,
  onCompletedChange,
}) => {
  const { data: identity } = useGetIdentity<{ name?: string; given_name?: string; family_name?: string }>()
  const updatedBy = identity?.name || `${identity?.given_name || ''} ${identity?.family_name || ''}`.trim() || 'admin'

  const [driverFieldsExpanded, setDriverFieldsExpanded] = useState(false)

  // Stage 1: fetch active opps
  const [activeOpps, setActiveOpps] = useState<ActiveOpp[] | null>(null)
  const [loadingOpps, setLoadingOpps] = useState(false)
  const [loadOppsError, setLoadOppsError] = useState<string | null>(null)

  // Stage 2: fan-out regen
  const [rows, setRows] = useState<OppRow[] | null>(null)
  const [regenLoading, setRegenLoading] = useState(false)
  const [regenDone, setRegenDone] = useState(false)

  // Stage 3: save all
  const [saveLoading, setSaveLoading] = useState(false)
  const [saveAllDone, setSaveAllDone] = useState(false)
  const [markerFired, setMarkerFired] = useState(false)

  // Profile edit modal
  const [editModalRow, setEditModalRow] = useState<OppRow | null>(null)
  const [editModalValue, setEditModalValue] = useState('')

  // Unsaved = at least one regenerated opp has non-empty new profile text that
  // has not been saved. Exact negation of the "Save all" disable condition.
  const dirty =
    !!rows &&
    rows.some((r) => r.newProfileText.trim() !== '' && r.saveStatus !== 'saved')
  // Report unsaved state to the parent; the cleanup clears the parent's guard
  // when this panel unmounts while dirty.
  useEffect(() => {
    onUnsavedChange?.(dirty)
    return () => {
      onUnsavedChange?.(false)
    }
  }, [dirty, onUnsavedChange])

  // Report completion (all targeted opps regenerated + saved → marker fired) so
  // the parent modal can unlock Approve (GH#1849 P1). Cleared on unmount.
  useEffect(() => {
    onCompletedChange?.(markerFired)
    return () => {
      onCompletedChange?.(false)
    }
  }, [markerFired, onCompletedChange])

  const handleDiscard = useCallback(() => {
    // Return the panel to its pre-regen state so the reviewer can approve the
    // funder edit without the fan-out regen (or re-run it).
    setActiveOpps(null)
    setRows(null)
    setRegenDone(false)
    setSaveAllDone(false)
    setLoadOppsError(null)
  }, [])

  const handleShowOpps = useCallback(async () => {
    setLoadingOpps(true)
    setLoadOppsError(null)
    try {
      // Use /admin/funders/{uuid} (fs_cli_read) which returns the funder with
      // embedded opportunities including flag_for_updates and tags.
      const res = await axios.get(`${apiUrl}/fs/funders/${targetUuid}`)
      const data = res.data as { opportunities?: Array<{
        uuid: string
        fund_name: string | null
        flag_for_updates: string | null
        profile_text?: string | null
        tags?: string[] | null
      }> }
      // The regen set must equal the backend advisory emit-set, or a successful
      // funder inline-regen marker suppresses advisories for opps never regenerated.
      const active = (data.opportunities ?? []).filter(isRegenerableOpportunity)
      setActiveOpps(
        active.map((o) => ({
          uuid: String(o.uuid),
          fund_name: o.fund_name ?? null,
          profile_text_length: o.profile_text ? o.profile_text.length : 0,
          tags: Array.isArray(o.tags) ? (o.tags as string[]) : [],
          flag_for_updates: o.flag_for_updates ?? null,
        })),
      )
    } catch (e: any) {
      setLoadOppsError(
        e?.response?.data?.detail ?? e?.message ?? 'Failed to load opportunities',
      )
    } finally {
      setLoadingOpps(false)
    }
  }, [targetUuid])

  const handleRegenAll = useCallback(async () => {
    if (!activeOpps) return
    setRegenLoading(true)

    // Initial rows state — all idle
    const initialRows: OppRow[] = activeOpps.map((o) => ({
      uuid: o.uuid,
      fund_name: o.fund_name,
      originalProfileLength: o.profile_text_length,
      originalTags: o.tags,
      newProfileText: '',
      newTags: [],
      generationFingerprint: '',
      profileStatus: 'idle',
      profileError: null,
      tagStatus: 'idle',
      tagError: null,
      saveStatus: 'idle',
      saveError: null,
      profileExpanded: false,
    }))
    setRows(initialRows)

    try {
      // Step 1: POST /opportunities/generate-profiles/{funder_uuid} with all opp uuids
      // The endpoint already parallelises with semaphore=5 server-side.
      const profileRes = await axios.post(
        `${apiUrl}/opportunities/generate-profiles/${targetUuid}`,
        {
          opportunity_uuids: activeOpps.map((o) => o.uuid),
          // Generate against the PROPOSED funder description under review, not
          // the stale DB value (the funder edit is still pending) — GH #1849 P3.
          funder_description: mergedDescription,
        },
      )

      const generatedProfiles: Array<{
        opportunity_uuid: string
        fund_name: string
        profile: string
        generation_fingerprint: string
      }> = profileRes.data?.generated_profiles ?? []

      const profileMap = new Map<
        string,
        { profile: string; generationFingerprint: string }
      >()
      for (const gp of generatedProfiles) {
        profileMap.set(String(gp.opportunity_uuid), {
          profile: gp.profile ?? '',
          generationFingerprint: gp.generation_fingerprint ?? '',
        })
      }

      // Update rows with profile results
      const afterProfileRows: OppRow[] = initialRows.map((r) => {
        const generated = profileMap.get(r.uuid)
        if (generated !== undefined) {
          const profile = generated.profile
          return {
            ...r,
            newProfileText: profile,
            generationFingerprint: generated.generationFingerprint,
            profileStatus: profile.trim() ? 'ok' : 'error',
            profileError: profile.trim()
              ? null
              : 'Profile generation returned empty text',
          }
        }
        // Not in generated_profiles means it errored on the backend
        return {
          ...r,
          profileStatus: 'error',
          profileError: 'Profile generation failed (no result from backend)',
        }
      })
      setRows([...afterProfileRows])

      // Step 2: per-opp tag regen — only for rows with non-empty profile
      const tagTasks = afterProfileRows.map((r) => async () => {
        if (!r.newProfileText.trim()) {
          return r // skip — profile is empty
        }
        try {
          const tagRes = await axios.post(`${apiUrl}/opportunities/tag/${r.uuid}`, {
            profile_text: r.newProfileText,
          })
          const result = tagRes.data as { tags: string[]; reasoning: string }
          return {
            ...r,
            newTags: result.tags ?? [],
            tagStatus: 'ok' as const,
            tagError: null,
          }
        } catch (e: any) {
          const detail =
            e?.response?.data?.detail ??
            e?.message ??
            'Failed to generate tags'
          return {
            ...r,
            tagStatus: 'error' as const,
            tagError: detail,
          }
        }
      })

      const tagResults = await runWithConcurrency(tagTasks, 5)
      setRows([...tagResults])
    } catch (e: any) {
      // Catastrophic profile regen failure — mark all rows as failed
      const errMsg =
        e?.response?.data?.detail ?? e?.message ?? 'Profile generation failed'
      setRows(
        initialRows.map((r) => ({
          ...r,
          profileStatus: 'error',
          profileError: errMsg,
        })),
      )
    } finally {
      setRegenLoading(false)
      setRegenDone(true)
    }
  }, [activeOpps, targetUuid, mergedDescription])

  const handleSaveAll = useCallback(async () => {
    if (!rows) return
    setSaveLoading(true)
    setSaveAllDone(false)

    const savableMask = rows.map(
      (r) => r.newProfileText.trim() !== '' && r.saveStatus !== 'saved',
    )

    const saveTasks = rows.map((r, idx) => async () => {
      if (!savableMask[idx]) return r
      try {
        await axios.post(
          `${apiUrl}/proposed-edits/${proposedEditId}/funder-inline-regen/${r.uuid}`,
          {
            profile_text: r.newProfileText,
            tags: r.newTags,
            generation_fingerprint: r.generationFingerprint,
            updated_by: updatedBy,
          },
        )
        return { ...r, saveStatus: 'saved' as const, saveError: null }
      } catch (e: any) {
        const raw = e?.response?.data?.detail ?? e?.message ?? 'Failed to save'
        const msg = typeof raw === 'string' ? raw : JSON.stringify(raw)
        return {
          ...r,
          saveStatus: 'error' as const,
          saveError: msg,
        }
      }
    })

    const savedRows = await runWithConcurrency(saveTasks, 5)
    setRows([...savedRows])

    // Each successful dedicated save transaction records that opportunity's
    // marker and clears only its duplicate advisory. The parent Approve gate
    // remains all-or-nothing until failed rows are retried or explicitly skipped.
    const allSaved =
      savedRows.length > 0 &&
      savedRows.every((r) => r.saveStatus === 'saved')
    setSaveAllDone(allSaved)
    setMarkerFired(allSaved)
    setSaveLoading(false)
  }, [rows, proposedEditId, updatedBy])

  const handleOpenEditModal = useCallback((row: OppRow) => {
    setEditModalRow(row)
    setEditModalValue(row.newProfileText)
  }, [])

  const handleCloseEditModal = useCallback(() => {
    setEditModalRow(null)
    setEditModalValue('')
  }, [])

  const handleSaveEditModal = useCallback(() => {
    if (!editModalRow || !rows) return
    setRows(
      rows.map((r) =>
        r.uuid === editModalRow.uuid
          ? { ...r, newProfileText: editModalValue }
          : r,
      ),
    )
    setEditModalRow(null)
    setEditModalValue('')
  }, [editModalRow, editModalValue, rows])

  return (
    <Box
      sx={{
        mt: 3,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Fan-out Profile + Tag Regeneration (Funder Description Change)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The funder description feeds every opportunity profile. Approving this
        edit may require regenerating profiles and tags for all active
        opportunities under this funder.
      </Typography>

      {/* Driver field collapsible */}
      <Box sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="text"
          startIcon={
            driverFieldsExpanded ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )
          }
          onClick={() => setDriverFieldsExpanded((v) => !v)}
          sx={{ textTransform: 'none', p: 0, color: 'text.secondary' }}
        >
          Driver field used
        </Button>
        <Collapse in={driverFieldsExpanded}>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {currentFunderDescription && (
              <TextField
                label="Current funder description"
                value={currentFunderDescription}
                multiline
                minRows={3}
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            )}
            <TextField
              label="Merged-pending funder description (will be used for regen)"
              value={mergedDescription}
              multiline
              minRows={3}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Confirm gate: show affected opps first */}
      {!activeOpps && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleShowOpps}
          disabled={loadingOpps}
          startIcon={loadingOpps ? <CircularProgress size={16} /> : null}
        >
          {loadingOpps ? 'Loading...' : 'Show affected opportunities'}
        </Button>
      )}

      {loadOppsError && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {loadOppsError}
        </Typography>
      )}

      {activeOpps && !regenDone && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{activeOpps.length}</strong> active{' '}
            {activeOpps.length === 1 ? 'opportunity' : 'opportunities'} under
            this funder will have profile + tags regenerated.
          </Typography>

          {activeOpps.length > 0 && (
            <TableContainer sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Opportunity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Profile length</TableCell>
                    <TableCell>Tags</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeOpps.map((o) => (
                    <TableRow key={o.uuid}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 220, overflowWrap: 'anywhere' }}>
                        {o.fund_name ?? o.uuid}
                      </TableCell>
                      <TableCell>{o.flag_for_updates}</TableCell>
                      <TableCell>{o.profile_text_length}</TableCell>
                      <TableCell>
                        {o.tags.length > 0
                          ? o.tags.slice(0, 3).join(', ') +
                            (o.tags.length > 3
                              ? ` +${o.tags.length - 3} more`
                              : '')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {activeOpps.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No active opportunities found under this funder. Nothing to
              regenerate.
            </Typography>
          ) : (
            <Button
              variant="contained"
              size="small"
              color="warning"
              onClick={handleRegenAll}
              disabled={regenLoading}
              startIcon={regenLoading ? <CircularProgress size={16} /> : null}
            >
              {regenLoading
                ? 'Regenerating...'
                : `Regenerate all (${activeOpps.length})`}
            </Button>
          )}
        </Box>
      )}

      {/* Per-opp results table after regen */}
      {regenDone && rows && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Regeneration results — review and save:
          </Typography>
          <TableContainer sx={{ mb: 2, maxHeight: 480, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Opportunity</TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell>Tags (old → new)</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.uuid}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, verticalAlign: 'top', maxWidth: 220, overflowWrap: 'anywhere' }}>
                      {r.fund_name ?? r.uuid}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', minWidth: 180 }}>
                      {r.profileStatus === 'error' ? (
                        <Typography variant="caption" color="error">
                          {r.profileError}
                        </Typography>
                      ) : r.newProfileText ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {r.originalProfileLength} → {r.newProfileText.length} chars
                          </Typography>
                          <Button
                            size="small"
                            variant="text"
                            sx={{ ml: 1, fontSize: 11, textTransform: 'none', p: 0 }}
                            onClick={() => handleOpenEditModal(r)}
                          >
                            View / edit
                          </Button>
                          {r.tagStatus === 'error' && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              Tags: {r.tagError}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', minWidth: 160 }}>
                      <Box>
                        {r.originalTags.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {r.originalTags.slice(0, 3).join(', ')}
                            {r.originalTags.length > 3 && ` +${r.originalTags.length - 3}`}
                          </Typography>
                        )}
                        {r.tagStatus === 'ok' && r.newTags.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.25 }}>
                            {r.newTags.slice(0, 4).map((tag) => (
                              <Chip key={tag} label={tag} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                            ))}
                            {r.newTags.length > 4 && (
                              <Typography variant="caption" color="text.secondary">
                                +{r.newTags.length - 4} more
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {r.saveStatus === 'saved' ? (
                        <Typography variant="caption" color="success.main">Saved</Typography>
                      ) : r.saveStatus === 'error' ? (
                        <Typography variant="caption" color="error">
                          Failed: {r.saveError}
                        </Typography>
                      ) : r.profileStatus === 'error' ? (
                        <Typography variant="caption" color="error">Profile failed</Typography>
                      ) : r.tagStatus === 'error' ? (
                        <Typography variant="caption" color="warning.main">Tag failed</Typography>
                      ) : r.profileStatus === 'ok' ? (
                        <Typography variant="caption" color="text.secondary">Ready</Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="contained"
            size="small"
            onClick={handleSaveAll}
            disabled={
              saveLoading ||
              rows.every(
                (r) =>
                  r.saveStatus === 'saved' ||
                  (!r.newProfileText.trim()),
              )
            }
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? 'Saving...' : 'Save all'}
          </Button>

          {dirty && (
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={handleDiscard}
              disabled={saveLoading}
              sx={{ ml: 1, textTransform: 'none' }}
            >
              Discard
            </Button>
          )}

          {saveAllDone && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Profiles and tags saved. You can now approve the proposed edit.
            </Typography>
          )}
        </Box>
      )}

      {/* Profile edit modal */}
      <Dialog
        open={!!editModalRow}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ overflowWrap: 'anywhere' }}>
          Edit profile — {editModalRow?.fund_name ?? editModalRow?.uuid}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Profile text"
            value={editModalValue}
            onChange={(e) => setEditModalValue(e.target.value)}
            multiline
            minRows={10}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEditModal}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
